import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tenantService, type Inquilino } from '@/services/tenantService';
import { debtService, type DeudaMensual } from '@/services/debtService';
import { paymentService, type RegistroPago } from '@/services/paymentService';
import { debtConceptService, type ConceptoDeDeuda } from '@/services/debtConceptService';
import { Button } from '@/components/ui/button';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import PaymentForm from '@/components/PaymentForm';
import DebtForm from '@/components/DebtForm';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';

const TenantDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Inquilino | null>(null);
  const [debts, setDebts] = useState<DeudaMensual[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaymentFormOpen, setIsPaymentFormOpen] = useState(false);
  const [currentDebtForPayment, setCurrentDebtForPayment] = useState<DeudaMensual | null>(null);
  const [isDebtFormOpen, setIsDebtFormOpen] = useState(false);
  const [expandedPayments, setExpandedPayments] = useState<Record<string, boolean>>({});

  const togglePayments = (debtId: string) => {
    setExpandedPayments(prev => ({
      ...prev,
      [debtId]: !prev[debtId]
    }));
  };

  const fetchTenantData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const fetchedTenant = await tenantService.getTenants().then(t => t.find(tn => tn.id_inquilino === id));
      if (fetchedTenant) {
        setTenant(fetchedTenant);
        const fetchedDebts = await debtService.getDebtsByInquilino(id);
        // Fetch concepts and payments for each debt
        const debtsWithConceptsAndPayments = await Promise.all(fetchedDebts.map(async (debt) => {
          const concepts = await debtConceptService.getConceptsByDebt(debt.id_deuda!);
          const payments = await paymentService.getPaymentsByDebt(debt.id_deuda!);
          return { ...debt, conceptos: concepts, payments: payments };
        }));
        setDebts(debtsWithConceptsAndPayments);
      } else {
        setError("Inquilino no encontrado.");
      }
    } catch (err: any) {
      setError(err.message);
      toast.error("Error al cargar los datos del inquilino.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenantData();
  }, [id]);

  const handleRegisterPayment = (debt: DeudaMensual) => {
    setCurrentDebtForPayment(debt);
    setIsPaymentFormOpen(true);
  };

  const handleSavePayment = async (payment: RegistroPago) => {
    try {
      // Create the payment record
      await paymentService.createPayment(payment);
      toast.success("Pago registrado exitosamente.");

      // Update the debt's saldo_pendiente and esta_pagada status
      const updatedSaldoPendiente = currentDebtForPayment!.saldo_pendiente - payment.monto_pagado;
      const updatedEstaPagada = updatedSaldoPendiente <= 0;

      await debtService.updateDebt(currentDebtForPayment!.id_deuda!, {
        saldo_pendiente: updatedSaldoPendiente,
        esta_pagada: updatedEstaPagada,
      });
      toast.success("Deuda actualizada.");

      fetchTenantData(); // Re-fetch data to update UI
    } catch (err: any) {
      toast.error(`Error al registrar el pago: ${err.message}`);
    }
  };

  const handleSaveDebt = async (debt: DeudaMensual, conceptos: ConceptoDeDeuda[]) => {
    try {
      await debtService.createDebt(debt, conceptos);
      toast.success("Deuda creada exitosamente.");
      fetchTenantData();
    } catch (err: any) {
      toast.error(`Error al crear la deuda: ${err.message}`);
    }
  };

  const breadcrumbs = [
    { label: "Inquilinos", path: "/tenants" },
    { label: tenant?.nombre_completo || "Cargando...", path: `/tenants/${id}` },
  ];

  if (loading) {
    return <div>Cargando detalles del inquilino...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!tenant) {
    return <div>Inquilino no encontrado.</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <Breadcrumbs items={breadcrumbs} />
      <Button onClick={() => navigate(-1)} className="mb-4">Volver</Button>
      <Button onClick={() => navigate(`/tenants/${id}/statement`)} className="mb-4 ml-2">Ver Estado de Cuenta</Button>
      <h1 className="text-2xl font-bold mb-4">Detalles del Inquilino: {tenant.nombre_completo}</h1>
      <p>Email: {tenant.email}</p>
      <p>Teléfono: {tenant.telefono}</p>
      <p>Activo: {tenant.activo ? 'Sí' : 'No'}</p>

      <h2 className="text-xl font-bold mt-8 mb-4">Deudas Mensuales</h2>
      <Button className="mb-4" onClick={() => setIsDebtFormOpen(true)}>Generar Nueva Deuda</Button>
      {debts.length === 0 ? (
        <p>No hay deudas registradas para este inquilino.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {debts.map((debt) => (
            <div key={debt.id_deuda} className="border p-4 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold">Deuda: {debt.mes_correspondiente}</h3>
              <p>Monto Total Adeudado: {debt.monto_total_adeudado}</p>
              <p>Saldo Pendiente: {debt.saldo_pendiente}</p>
              <p>Pagada: {debt.esta_pagada ? 'Sí' : 'No'}</p>
              {debt.conceptos && debt.conceptos.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold mt-2">Conceptos:</h4>
                  <ul className="list-disc pl-5">
                    {debt.conceptos.map(concept => (
                      <li key={concept.id_concepto_deuda}>{concept.tipo}: {concept.monto} ({concept.descripcion})</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-4 flex space-x-2">
                <Button variant="secondary" onClick={() => handleRegisterPayment(debt)}>Registrar Pago</Button>
                <Button variant="destructive">Eliminar Deuda</Button>
                <Button
                  variant="outline"
                  onClick={() => togglePayments(debt.id_deuda!)}
                  disabled={!debt.payments || debt.payments.length === 0}
                >
                  {(!debt.payments || debt.payments.length === 0) ? 'No hay pagos' : (expandedPayments[debt.id_deuda!] ? 'Ocultar Pagos' : 'Ver Pagos')}
                </Button>
              </div>
              {expandedPayments[debt.id_deuda!] && debt.payments && debt.payments.length > 0 && (
                <div className="mt-4 p-3 bg-gray-50 rounded-md">
                  <h4 className="text-md font-semibold mb-2">Pagos Realizados:</h4>
                  <ul className="list-disc pl-5">
                    {debt.payments.map((payment) => (
                      <li key={payment.id_registro_pago}>
                        {payment.fecha_pago.split('T')[0]} - {formatCurrency(payment.monto_pagado)} ({payment.metodo_pago})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {expandedPayments[debt.id_deuda!] && (!debt.payments || debt.payments.length === 0) && (
                <div className="mt-4 p-3 bg-gray-50 rounded-md">
                  <p>No hay pagos registrados para esta deuda.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <PaymentForm
        isOpen={isPaymentFormOpen}
        onClose={() => setIsPaymentFormOpen(false)}
        onSave={handleSavePayment}
        debtId={currentDebtForPayment?.id_deuda || ''}
      />
      <DebtForm
        isOpen={isDebtFormOpen}
        onClose={() => setIsDebtFormOpen(false)}
        onSave={handleSaveDebt}
        inquilinoId={id!}
        edificioId={tenant.unidad?.id_edificio!} // Pass the building ID from the tenant's unit
      />
    </div>
  );
};

export default TenantDetailPage;